import { currencyGet, ApiShapeError } from '@/lib/task-api';
import { parseCount, parseNumeric, rateSum, sumRates, volumeOf } from '@/lib/report-volume';
import type { TotalAmount, UnitRate } from '@/lib/money';
// Sayfalama sozlesmesi TEK yerde tanimli (admin liste ekranlari da ayni tipleri kullaniyor);
// burada yeniden yazmak "page ne icerir" sorusunu ikiye bolerdi.
import type { PageMeta, Paged } from '@/features/admin/api';
import { pairOf } from '@/lib/task-currency';

/**
 * ======================= ISLETME RAPORU UCLARI ==============================
 *
 * Uc uc, hepsi ORGANIZASYON kapsaminda (kapsam cagiranin TOKEN'indan cozuluyor;
 * istemci organizationId GONDEREMEZ — gonderseydi bir buro baskasinin cirosunu
 * okurdu). `from` / `to` opsiyonel ama GECERSIZ tarihte sunucu 400 doner.
 *
 * CANLI SUNUCUYA KARSI DOGRULANMIS YANIT SEKILLERI:
 *
 *   GET /v1/reports/offers-summary   (yetki: viewReport)
 *     { code:'success', summaryList: [
 *         { taskTypeCode:'USD', offerCount:2711, wonCount:1397,
 *           totalOfferPrice:'119767.21', wonOfferPrice:'61611.74' }, … ] }
 *
 *   GET /v1/reports/tasks-summary    (yetki: viewReport)
 *     { code:'success', summaryList: [
 *         { taskStatusCode:'completed', taskCount:2963, totalPrice:'129494.85' }, … ] }
 *
 *   GET /v1/reports/member-activity  (YALNIZ ORG YONETICISI — yetki DEGIL)
 *     { code:'success', activityList: [
 *         { appUserId:'5', userFullName:'…', organizationMemberTypeCode:'staff',
 *           memberIsActive:true, offerCount:3032, wonCount:1594,
 *           wonOfferPrice:'69439.84' }, … ] }
 *
 * ------------------- DIKKAT: BU TUTARLAR HACIM DEGIL ------------------------
 * Uc SQL de `COALESCE(sum(o.offer_price), 0)` yaziyor — MIKTARLA CARPIM YOK.
 * Yani `totalOfferPrice` / `wonOfferPrice` / `totalPrice` BIRIM KURLARIN
 * TOPLAMI, ciro degil. Bu yuzden `rateSum()` ile UnitRate isaretleniyorlar ve
 * <MoneyText total=…> icine gecirilmeleri DERLEME HATASI oluyor.
 * Ayrintili gerekce: src/lib/report-volume.ts.
 *
 * `appUserId` STRING KALIR: pg int8'i string dondurur, sayiya cevirmek 2^53
 * ustunde sessizce yanlis sonuc verir (bkz. src/auth/types.ts).
 * ========================================================================== */

type RawEnvelope = { code?: string; message?: string } & Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Liste alanini SEKLINI DOGRULAYARAK cikarir — `?? []` YAZILMAZ.
 *
 * Alan adi degisirse (`summaryList` -> `items`) `?? []` ekrani hatasiz ama BOS
 * gosterirdi: "bu donemde teklif yok". Ne tsc ne test yakalar. `expectItems`
 * (src/lib/task-api.ts) ayni sebeple `items` icin firlatiyor; bu uclar farkli
 * alan adlari kullandigi icin ayni desenin adlandirilmis hali burada.
 */
const expectList = (data: RawEnvelope, field: string, endpoint: string): unknown[] => {
  const list = data[field];
  if (!Array.isArray(list)) throw new ApiShapeError(`${endpoint}.${field} dizi degil`);
  return list;
};

const expectRow = (raw: unknown, endpoint: string, index: number): Record<string, unknown> => {
  if (!isRecord(raw)) throw new ApiShapeError(`${endpoint}[${index}] nesne degil`);
  return raw;
};

/** Sunucudan gelen kod alanlari (para birimi, durum, uye tipi) serbest metin. */
const asCode = (value: unknown, field: string): string => {
  if (typeof value === 'string' && value !== '') return value;
  throw new ApiShapeError(`${field} bos olmayan bir dize degil`);
};

const asId = (value: unknown, field: string): string => {
  if (typeof value === 'string' && value !== '') return value;
  // pg int8'i string doner; kucuk kimliklerde number gorulebiliyor.
  if (typeof value === 'number') return String(value);
  throw new ApiShapeError(`${field} kimlik degil`);
};

/* -------------------------------------------------------------------------- */
/* Teklif ozeti — para birimi kirilimi                                         */
/* -------------------------------------------------------------------------- */

export type OfferSummaryRow = Readonly<{
  /** Para birimi kodu ('USD', 'EUR/USD'…). */
  currency: string;
  offerCount: number;
  wonCount: number;
  /** BIRIM KURLARIN TOPLAMI — hacim DEGIL. */
  totalOfferRate: UnitRate;
  /** Kazanilan tekliflerin BIRIM KUR toplami — hacim DEGIL. */
  wonOfferRate: UnitRate;
}>;

export type OffersSummary = Readonly<{
  rows: readonly OfferSummaryRow[];
  offerCount: number;
  wonCount: number;
  wonOfferRate: UnitRate;
}>;

const ENDPOINT_OFFERS = 'reports/offers-summary';

export const parseOffersSummary = (data: RawEnvelope): OffersSummary => {
  const rows = expectList(data, 'summaryList', ENDPOINT_OFFERS).map((raw, index) => {
    const row = expectRow(raw, `${ENDPOINT_OFFERS}.summaryList`, index);
    return {
      currency: asCode(row.taskTypeCode, `${ENDPOINT_OFFERS}[${index}].taskTypeCode`),
      offerCount: parseCount(row.offerCount, `${ENDPOINT_OFFERS}[${index}].offerCount`),
      wonCount: parseCount(row.wonCount, `${ENDPOINT_OFFERS}[${index}].wonCount`),
      totalOfferRate: rateSum(
        row.totalOfferPrice,
        `${ENDPOINT_OFFERS}[${index}].totalOfferPrice`,
      ),
      wonOfferRate: rateSum(row.wonOfferPrice, `${ENDPOINT_OFFERS}[${index}].wonOfferPrice`),
    };
  });

  return {
    // En cok kazanilan kur toplami ustte: bar serisi sirali gelmeli, sunucu
    // alfabetik (task_type_code) siraliyor.
    rows: [...rows].sort((a, b) => b.wonOfferRate - a.wonOfferRate),
    offerCount: rows.reduce((acc, row) => acc + row.offerCount, 0),
    wonCount: rows.reduce((acc, row) => acc + row.wonCount, 0),
    wonOfferRate: sumRates(rows.map((row) => row.wonOfferRate)),
  };
};

/**
 * `to` ONCEKI DONEM ICIN EKLENDI (2026-08-30).
 *
 * Secili donem icin yalniz `from` gonderiliyor ("su tarihten bugune"). Kiyas
 * penceresi ise KAPALI olmak zorunda: ust siniri verilmezse "onceki donem"
 * bugune kadar uzanir, secili donemi de icine alir ve kiyas kendi kendini
 * sulandirir. Sunucu `to`'yu zaten destekliyor (bkz. lib/report-range.ts).
 *
 * Ikisi de opsiyonel ve BAGIMSIZ: yalniz `from`, yalniz `to`, ikisi ya da
 * hicbiri gonderilebilir. Bos dize GONDERILMEZ — sunucu onu gecersiz tarih
 * sayip 400 doner.
 */
export const fetchOffersSummary = async (
  from: string | undefined,
  to?: string,
): Promise<OffersSummary> => {
  const params: Record<string, string> = {};
  if (from !== undefined) params.from = from;
  if (to !== undefined) params.to = to;
  const { data } = await currencyGet<RawEnvelope>(
    '/v1/reports/offers-summary',
    Object.keys(params).length === 0 ? undefined : params,
  );
  return parseOffersSummary(data);
};

/* -------------------------------------------------------------------------- */
/* Islem ozeti — durum kirilimi                                                */
/* -------------------------------------------------------------------------- */

export type TaskSummaryRow = Readonly<{
  /** Sunucu durum kodu ('completed', 'pending', 'warning'…). */
  statusCode: string;
  taskCount: number;
  /** BIRIM KURLARIN TOPLAMI — hacim DEGIL. */
  totalRate: UnitRate;
}>;

export type TasksSummary = Readonly<{ rows: readonly TaskSummaryRow[]; taskCount: number }>;

const ENDPOINT_TASKS = 'reports/tasks-summary';

export const parseTasksSummary = (data: RawEnvelope): TasksSummary => {
  const rows = expectList(data, 'summaryList', ENDPOINT_TASKS).map((raw, index) => {
    const row = expectRow(raw, `${ENDPOINT_TASKS}.summaryList`, index);
    return {
      statusCode: asCode(row.taskStatusCode, `${ENDPOINT_TASKS}[${index}].taskStatusCode`),
      taskCount: parseCount(row.taskCount, `${ENDPOINT_TASKS}[${index}].taskCount`),
      totalRate: rateSum(row.totalPrice, `${ENDPOINT_TASKS}[${index}].totalPrice`),
    };
  });

  return {
    rows: [...rows].sort((a, b) => b.taskCount - a.taskCount),
    taskCount: rows.reduce((acc, row) => acc + row.taskCount, 0),
  };
};

export const fetchTasksSummary = async (from: string | undefined): Promise<TasksSummary> => {
  const { data } = await currencyGet<RawEnvelope>(
    '/v1/reports/tasks-summary',
    from === undefined ? undefined : { from },
  );
  return parseTasksSummary(data);
};

/* -------------------------------------------------------------------------- */
/* Personel performansi — YALNIZ ORG YONETICISI                                */
/* -------------------------------------------------------------------------- */

export type MemberActivityRow = Readonly<{
  /** KULLANICI kimligi — string (pg int8). */
  appUserId: string;
  userFullName: string;
  /** 'admin' | 'staff' — sunucu kodu, ceviri labels.ts'te. */
  memberTypeCode: string;
  /** Uyeligi pasiflesmis personel LISTEDE KALIR (gecmis donem eksilmesin). */
  isActive: boolean;
  offerCount: number;
  wonCount: number;
  /** BIRIM KURLARIN TOPLAMI — hacim DEGIL. */
  wonOfferRate: UnitRate;
}>;

const ENDPOINT_MEMBERS = 'reports/member-activity';

export const parseMemberActivity = (data: RawEnvelope): readonly MemberActivityRow[] =>
  expectList(data, 'activityList', ENDPOINT_MEMBERS).map((raw, index) => {
    const row = expectRow(raw, `${ENDPOINT_MEMBERS}.activityList`, index);
    return {
      appUserId: asId(row.appUserId, `${ENDPOINT_MEMBERS}[${index}].appUserId`),
      userFullName: typeof row.userFullName === 'string' ? row.userFullName : '',
      memberTypeCode:
        typeof row.organizationMemberTypeCode === 'string' ? row.organizationMemberTypeCode : '',
      // Yalniz acik `false` pasif sayilir; alan hic gelmezse aktif varsayilir.
      isActive: row.memberIsActive !== false,
      offerCount: parseCount(row.offerCount, `${ENDPOINT_MEMBERS}[${index}].offerCount`),
      wonCount: parseCount(row.wonCount, `${ENDPOINT_MEMBERS}[${index}].wonCount`),
      wonOfferRate: rateSum(row.wonOfferPrice, `${ENDPOINT_MEMBERS}[${index}].wonOfferPrice`),
    };
  });

export const fetchMemberActivity = async (
  from: string | undefined,
): Promise<readonly MemberActivityRow[]> => {
  const { data } = await currencyGet<RawEnvelope>(
    '/v1/reports/member-activity',
    from === undefined ? undefined : { from },
  );
  return parseMemberActivity(data);
};

/* -------------------------------------------------------------------------- */
/* Uyari alan islemler — GET /v1/reports/warned-tasks                          */
/* -------------------------------------------------------------------------- */

/**
 * "Odeme yapilmadi" uyarisi alan, BURONUN KAZANDIGI islemler.
 *
 * `viewReport` yetiyor, yonetici sarti YOK: uyari buronun kazandigi islemde olusur ve musteri
 * kimligi kazanan buroya zaten gorunur durumda — gizlenecek yeni bir sey yok.
 *
 * SAYFALANIR: rapor ailesindeki TEK sayfalanan uc. Digerleri agregat (para birimi / durum /
 * personel kirilimi) ve satir sayilari sinirli; bu ise donem uzadikca sinirsiz buyuyen bir
 * dokum. Onceden sunucu sabit `LIMIT 50` uyguluyordu ve elli birinci uyari ekrandan SESSIZCE
 * dusuyordu — hicbir yerde "kirpildi" yazmadigi icin liste TAM gorunuyordu.
 *
 * Zarf, /v1/tasks ve denetim kaydiyla AYNI: `{ items, page }`. `total` YOK, dolayisiyla sayfa
 * sayaci da yok; yalniz `hasMore` uzerinden "daha fazla yukle".
 *
 * TUTAR GERCEK HACIM: `taskAmount * offerPrice`. Sunucu ikisini ayri donduruyor, carpim burada
 * yapiliyor ve `volumeOf` ile TotalAmount isaretleniyor — `offerPrice` tek basina BIRIM KUR'dur
 * ve toplam gibi cizilmesi derleme hatasi verir.
 */
const ENDPOINT_WARNED = '/v1/reports/warned-tasks';

/** Sunucunun kabul ettigi siralama anahtarlari (beyaz liste sunucuda; bilinmeyen anahtar 400). */
export const WARNED_SORTS = [
  'warnedAt:desc',
  'warnedAt:asc',
  'volume:desc',
  'volume:asc',
  'customerName:asc',
] as const;
export type WarnedSort = (typeof WARNED_SORTS)[number];
export const WARNED_SORT_DEFAULT: WarnedSort = 'warnedAt:desc';

export const WARNED_PAGE_SIZE = 20;

export type WarnedTaskRow = Readonly<{
  taskId: string;
  taskTypeCode: string;
  amount: number;
  volume: TotalAmount;
  /**
   * `volume`in BIRIMI (karsi para birimi; EUR/USD'de USD, digerlerinde TRY). 2026-09-23'e
   * kadar ekran her satiri "TRY" diye basiyordu — EUR/USD isleminin USD tutari TL gorunuyordu.
   */
  counterCurrency: string;
  warnedAt: string | null;
  customerName: string;
}>;

const toWarnedRow = (raw: unknown, index: number): WarnedTaskRow => {
  const row = expectRow(raw, `${ENDPOINT_WARNED}.items`, index);
  const amount = parseNumeric(row.taskAmount, `${ENDPOINT_WARNED}[${index}].taskAmount`);
  const rate = rateSum(row.offerPrice, `${ENDPOINT_WARNED}[${index}].offerPrice`);
  const taskTypeCode = asCode(row.taskTypeCode, `${ENDPOINT_WARNED}[${index}].taskTypeCode`);
  return {
    taskId: asId(row.taskId, `${ENDPOINT_WARNED}[${index}].taskId`),
    taskTypeCode,
    amount,
    volume: volumeOf(rate, amount),
    // Sunucu gonderir; eski bir surum gondermezse tipten turetilir (ayni tablo: task_type).
    counterCurrency:
      typeof row.counterCurrency === 'string' && row.counterCurrency !== ''
        ? row.counterCurrency
        : pairOf(taskTypeCode).secondCurrency,
    warnedAt: typeof row.warnedAt === 'string' ? row.warnedAt : null,
    // Kurumsal ad varsa o, yoksa kisi adi (piyasa kartlarindaki ayni tercih).
    customerName:
      (typeof row.userBusinessName === 'string' && row.userBusinessName !== ''
        ? row.userBusinessName
        : typeof row.userFullName === 'string'
          ? row.userFullName
          : ''),
  };
};

/**
 * `page` zarfi. `?? {}` YAZILMAZ — eksik `page`, "devami yok" diye okunur ve kullanici
 * listenin ilk yirmi satirini TAM liste sanirdi. Ayni sebeple `items` de `expectList` ile
 * dogrulanir (bkz. bu dosyanin basindaki gerekce).
 */
const expectWarnedPage = (raw: unknown, fallbackReturned: number): PageMeta => {
  if (!isRecord(raw)) throw new ApiShapeError(`${ENDPOINT_WARNED}.page nesne degil`);
  const asCount = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return {
    limit: asCount(raw.limit) || WARNED_PAGE_SIZE,
    offset: asCount(raw.offset),
    returned: raw.returned === undefined ? fallbackReturned : asCount(raw.returned),
    hasMore: raw.hasMore === true,
    snapshotAt: typeof raw.snapshotAt === 'string' ? raw.snapshotAt : null,
  };
};

export const fetchWarnedTasks = async (input: {
  from: string | undefined;
  offset: number;
  snapshotAt: string | null;
  sort: WarnedSort;
}): Promise<Paged<WarnedTaskRow>> => {
  const { data } = await currencyGet<RawEnvelope>(ENDPOINT_WARNED, {
    ...(input.from === undefined ? {} : { from: input.from }),
    limit: WARNED_PAGE_SIZE,
    offset: input.offset,
    sort: input.sort,
    // `snapshotAt` YALNIZ VARSA gonderilir: ilk sayfada henuz yok, bos dize gondermek
    // sunucuda INVALID_SNAPSHOT_AT olurdu.
    ...(input.snapshotAt === null ? {} : { snapshotAt: input.snapshotAt }),
  });
  const items = expectList(data, 'items', ENDPOINT_WARNED).map(toWarnedRow);
  return { items, page: expectWarnedPage(data.page, items.length) };
};

/* -------------------------------------------------------------------------- */
/* Ortalama karar suresi — GET /v1/reports/decision-time                       */
/* -------------------------------------------------------------------------- */

/**
 * Talep acilisindan teklifin kabulune gecen ortalama sure (buronun kazandigi islemler).
 *
 * `decidedCount` OLMADAN ortalama okunamaz: iki islemin ortalamasi ile iki yuz islemin
 * ortalamasi ayni sayiyi gosterse bile ayni sey degildir. Sunucu ikisini birlikte donuyor,
 * ekran da birlikte gostermeli.
 *
 * `decided_at` migration'indan ONCE biten islemler ortalamaya GIRMEZ (kolon NULL) — yani
 * gecmise dogru eksik bir olcum; ekranda bu not olmadan gosterilmemeli.
 */
const ENDPOINT_DECISION = '/v1/reports/decision-time';

export type DecisionTime = Readonly<{
  decidedCount: number;
  avgSeconds: number;
}>;

export const fetchDecisionTime = async (
  from: string | undefined,
): Promise<DecisionTime> => {
  const { data } = await currencyGet<RawEnvelope>(
    ENDPOINT_DECISION,
    from === undefined ? undefined : { from },
  );
  const raw = data.decisionTime;
  if (!isRecord(raw)) throw new ApiShapeError(`${ENDPOINT_DECISION}.decisionTime nesne degil`);
  return {
    decidedCount: parseCount(raw.decidedCount, `${ENDPOINT_DECISION}.decidedCount`),
    avgSeconds: parseCount(raw.avgDecisionSeconds, `${ENDPOINT_DECISION}.avgDecisionSeconds`),
  };
};
